import { useState } from 'react';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LocationOption } from '../types';
import { searchLocations } from '../api/atriuslink';
import { LocationCreator } from './LocationCreator';
import { LocationGridMessage } from './LocationGridMessage';
import { ArrowUpIcon, ArrowDownIcon, Pencil, Trash2, Plus, Search } from 'lucide-react';

interface LocationEditorProps {
  locations: LocationOption[];
  onUpdate: (locations: LocationOption[]) => void;
}

interface SearchResult {
  poiTypes: string[];
  pois: {
    id?: string;
    poiId?: string;
    name?: string;
    description?: string;
    type?: string;
    category?: string;
    position?: {
      floorId?: string;
      latitude?: number;
      longitude?: number;
      structureName?: string;
      buildingId?: string;
      floorName?: string;
      floorOrdinal?: number;
    };
    operationHours?: string;
    phone?: string;
    nearbyLandmark?: string;
    keywords?: {
      isDisplayed: boolean;
      isUserSearchable: boolean;
      name: string;
    }[];
    images?: string[];
  }[];
}

export function LocationEditor({ locations, onUpdate }: LocationEditorProps) {
  const [showLocationCreator, setShowLocationCreator] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPOIs, setSelectedPOIs] = useState<Set<string>>(new Set());
  const [selectedTerminal, setSelectedTerminal] = useState<string>('');

  const handleMoveLocation = (fromIndex: number, toIndex: number) => {
    console.log('Moving location from index', fromIndex, 'to index', toIndex);
    const newLocations = [...locations];
    const [movedItem] = newLocations.splice(fromIndex, 1);
    newLocations.splice(toIndex, 0, movedItem);
    console.log('Updated locations after move:', newLocations);
    onUpdate(newLocations);
  };

  const handleDeleteLocation = (index: number) => {
    console.log('Deleting location at index:', index);
    const newLocations = locations.filter((_, i) => i !== index);
    console.log('Updated locations after delete:', newLocations);
    onUpdate(newLocations);
  };

  const handleEditLocation = (location: LocationOption, index: number) => {
    console.log('Editing location at index:', index, 'with data:', location);
    const newLocations = [...locations];
    newLocations[index] = location;
    console.log('Updated locations after edit:', newLocations);
    onUpdate(newLocations);
    setEditingIndex(null);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const results = await searchLocations({
        message: searchQuery,
        venueId: 'ewr',
      });
      console.log('Search results:', results);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      // TODO: Add error message UI
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectPOI = (poiId: string) => {
    setSelectedPOIs(prev => {
      const next = new Set(prev);
      if (next.has(poiId)) {
        next.delete(poiId);
      } else {
        next.add(poiId);
      }
      return next;
    });
  };

  const handleAddSelectedPOIs = () => {
    if (!searchResults) return;
    
    const selectedLocations = searchResults.pois
      .filter(poi => {
        const poiId = poi.id || poi.poiId;
        return poiId && selectedPOIs.has(poiId);
      })
      .map(poi => {
        // Get displayed keywords
        const displayedKeywords = poi.keywords
          ?.filter(k => k.isDisplayed)
          .map(k => k.name) || [];

        // Build subtitle with category and terminal info
        const subtitleParts = [
          poi.type || poi.category,
          poi.position?.structureName,
          poi.position?.floorName
        ].filter(Boolean);

        return {
          type: 'LOCATION' as const,
          title: poi.name || '',
          subtitle: subtitleParts.join(' • '),
          imgUrl: poi.images?.[0],
          locationId: poi.id || poi.poiId || '',
          label: poi.type || poi.category || '',
          description: poi.description || ''
        };
      });
    
    console.log('Adding new locations:', selectedLocations);
    const newLocations = [...locations, ...selectedLocations];
    console.log('Updated locations after add:', newLocations);
    onUpdate(newLocations);
    setSearchResults(null);
    setShowSearch(false);
    setSearchQuery('');
    setSelectedPOIs(new Set());
    setSelectedTerminal('');
  };

  return (
    <div className="space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
      {/* Existing Locations */}
      {locations.length > 0 && (
        <div className="grid grid-cols-3 gap-2 p-1">
          {locations.map((location, index) => (
            <div key={location.locationId || `location-${index}`} className="relative group">
              <div className="w-full">
                <LocationGridMessage locations={[location]} />
              </div>
              
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded-lg">
                <div className="flex gap-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleMoveLocation(index, Math.max(0, index - 1))}
                    disabled={index === 0}
                    className="hover:bg-white/30 h-7 w-7"
                  >
                    <ArrowUpIcon className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleMoveLocation(index, Math.min(locations.length - 1, index + 1))}
                    disabled={index === locations.length - 1}
                    className="hover:bg-white/30 h-7 w-7"
                  >
                    <ArrowDownIcon className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEditingIndex(index)}
                    className="hover:bg-white/30 h-7 w-7"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDeleteLocation(index)}
                    className="hover:bg-red-500/70 h-7 w-7"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Location Creator or Search */}
      {showLocationCreator || editingIndex !== null ? (
        <LocationCreator
          onAdd={(location) => {
            if (editingIndex !== null) {
              handleEditLocation(location, editingIndex);
            } else {
              console.log('Adding new location:', location);
              const newLocations = [...locations, location];
              console.log('Updated locations after add:', newLocations);
              onUpdate(newLocations);
            }
            setShowLocationCreator(false);
          }}
          onCancel={() => {
            setShowLocationCreator(false);
            setEditingIndex(null);
          }}
          initialLocation={editingIndex !== null ? locations[editingIndex] : undefined}
        />
      ) : showSearch ? (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Search for locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              variant="secondary"
              onClick={handleSearch}
              disabled={isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowSearch(false)}
            >
              Cancel
            </Button>
          </div>
          
          {searchResults && (
            <div className="space-y-4">
              {searchResults.pois.length > 0 ? (
                <>
                  {/* Terminal Filter */}
                  <div className="flex items-center gap-2">
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      value={selectedTerminal}
                      onChange={(e) => setSelectedTerminal(e.target.value)}
                    >
                      <option value="">All Terminals</option>
                      {Array.from(new Set(searchResults.pois
                        .map(poi => poi.position?.structureName)
                        .filter(Boolean)))
                        .sort()
                        .map(terminal => (
                          <option key={terminal} value={terminal}>
                            {terminal}
                          </option>
                        ))
                      }
                    </select>
                    {selectedTerminal && (
                      <button
                        className="text-xs text-gray-500 hover:text-gray-700"
                        onClick={() => setSelectedTerminal('')}
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {searchResults.pois
                      .filter(poi => !selectedTerminal || poi.position?.structureName === selectedTerminal)
                      .map((poi, index) => {
                        const poiId = poi.id || poi.poiId;
                        // Skip POIs without any ID
                        if (!poiId) {
                          console.warn('Found POI without ID:', poi);
                          return null;
                        }
                        
                        return (
                          <div
                            key={`search-${poiId}`}
                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                              selectedPOIs.has(poiId) 
                                ? 'bg-blue-50 border-blue-200' 
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => handleSelectPOI(poiId)}
                          >
                            <div className="font-medium">{poi.name}</div>
                            <div className="text-sm text-gray-500 mt-1">
                              {poi.type || poi.category}
                              {poi.position?.structureName && (
                                <span className="ml-1">• {poi.position.structureName}</span>
                              )}
                              {poi.position?.floorName && (
                                <span className="ml-1">• {poi.position.floorName}</span>
                              )}
                            </div>
                            {poi.description && (
                              <div className="text-xs text-gray-600 mt-1">{poi.description}</div>
                            )}
                            {(poi.operationHours || poi.phone || poi.nearbyLandmark) && (
                              <div className="mt-2 space-y-1">
                                {poi.operationHours && (
                                  <div className="text-xs">
                                    <span className="font-medium">Hours:</span> {poi.operationHours}
                                  </div>
                                )}
                                {poi.phone && (
                                  <div className="text-xs">
                                    <span className="font-medium">Phone:</span> {poi.phone}
                                  </div>
                                )}
                                {poi.nearbyLandmark && (
                                  <div className="text-xs">
                                    <span className="font-medium">Near:</span> {poi.nearbyLandmark}
                                  </div>
                                )}
                              </div>
                            )}
                            {poi.keywords?.some(k => k.isDisplayed) && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {poi.keywords
                                  .filter(k => k.isDisplayed)
                                  .map(k => (
                                    <span 
                                      key={k.name}
                                      className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                                    >
                                      {k.name}
                                    </span>
                                  ))
                                }
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  {selectedPOIs.size > 0 && (
                    <div className="flex justify-end">
                      <Button 
                        variant="default"
                        onClick={handleAddSelectedPOIs}
                      >
                        Add {selectedPOIs.size} Location{selectedPOIs.size === 1 ? '' : 's'}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-gray-500">No results found</div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setShowLocationCreator(true)}
          >
            Add Location Manually
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setShowSearch(true)}
          >
            <Search className="h-4 w-4 mr-2" />
            Search Locations
          </Button>
        </div>
      )}
    </div>
  );
}
